import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRightIcon, LockIcon, XIcon } from 'lucide-react';
import { strategy } from '../../data/strategy';
interface StrategyOverlayProps {
  onClose: () => void;
}
const EASE = [0.23, 1, 0.32, 1] as const;
function Contrast({
  from,
  to
}: {
  from: string;
  to: string;
}) {
  return <div className="mt-3 flex flex-col gap-2 rounded-xl border border-line bg-mist px-4 py-3 sm:flex-row sm:items-center" data-id="element-378" data-mp="331tags19scx3">
      <p className="flex-1 text-[12px] leading-snug text-mut line-through decoration-mut/40" data-id="element-379" data-mp="26aqyxraivli8">
        {from}
      </p>
      <ArrowRightIcon className="h-3.5 w-3.5 shrink-0 text-blue" aria-hidden="true" data-id="element-380" data-mp="2fdidw12lwzem" />
      <p className="flex-1 text-[12.5px] font-semibold leading-snug text-navy" data-id="element-381" data-mp="2fbfewrjh7vxr">{to}</p>
    </div>;
}
export function StrategyOverlay({
  onClose
}: StrategyOverlayProps) {
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} transition={{
    duration: 0.18,
    ease: EASE
  }} className="fixed inset-0 z-50 overflow-y-auto bg-navy/45 px-4 py-8 backdrop-blur-[2px]" role="dialog" aria-modal="true" aria-label={strategy.title} data-id="element-382" data-mp="1mokv7wuhh0lc">
      <motion.article initial={{
      opacity: 0,
      y: 12
    }} animate={{
      opacity: 1,
      y: 0
    }} exit={{
      opacity: 0,
      y: 8
    }} transition={{
      duration: 0.22,
      ease: EASE
    }} className="mx-auto w-full max-w-2xl overflow-hidden rounded-[24px] bg-paper shadow-soft" data-id="element-383" data-mp="1xzuliym7361a">
        <header className="flex items-start justify-between gap-4 bg-navy px-7 py-6" data-id="element-384" data-mp="1j2k12zrkqptf">
          <div className="min-w-0" data-id="element-385" data-mp="1j4wss4n4wejo">
            <p className="flex items-center gap-1.5 text-[9px] uppercase tracking-micro text-mint" data-id="element-386" data-mp="1f0gqsegn5r9q">
              <LockIcon className="h-3 w-3" aria-hidden="true" data-id="element-387" data-mp="1a2nj1gzowp7c" />
              {strategy.eyebrow}
            </p>
            <h2 className="mt-2.5 font-display text-[22px] font-semibold leading-tight tracking-tight text-paper" data-id="element-388" data-mp="39dyga70lwy7e">
              {strategy.title}
            </h2>
          </div>
          <button type="button" onClick={onClose} aria-label={strategy.close} className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-paper/30 text-paper/80 transition-colors duration-150 hover:border-mint hover:text-paper focus:outline-none focus-visible:ring-2 focus-visible:ring-mint" data-id="element-389" data-mp="3nk00fxck1l9s">
            <XIcon className="h-4 w-4" aria-hidden="true" data-id="element-390" data-mp="b34tnr6ab82w" />
          </button>
        </header>

        <div className="px-7 py-7" data-id="element-391" data-mp="f4q3f3niv68v">
          {strategy.intro.map(text => <p key={text} className="mt-0 text-[13.5px] leading-relaxed text-navy2 [&+p]:mt-3" data-id="element-392" data-mp="f2m12q59ufap">
              {text}
            </p>)}

          <div className="mt-5 grid gap-3 sm:grid-cols-3" data-id="element-393" data-mp="638rlxh5dqpz">
            {strategy.pillars.map((pillar, i) => <div key={pillar.title} className="rounded-xl border border-line bg-mist px-4 py-4" data-id="element-394" data-mp="2lu4oo5vj6zcp" data-mpi={i}>
                <p className="font-display text-[10px] font-bold tabular-nums text-blue" data-id="element-395" data-mp="2qrwhr37wwrih">
                  0{i + 1}
                </p>
                <p className="mt-1.5 font-display text-[13.5px] font-semibold tracking-tight text-navy" data-id="element-396" data-mp="2ecgam4h5ynmk">
                  {pillar.title}
                </p>
                <p className="mt-2 text-[12px] leading-relaxed text-mut" data-id="element-397" data-mp="2s926j9xgy2ec">{pillar.body}</p>
              </div>)}
          </div>

          <div className="mt-6 rounded-xl border border-blue/40 bg-blue/[0.06] px-5 py-5" data-id="element-398" data-mp="1zlzaqpk6g5rs">
            <p className="font-display text-[17px] font-semibold leading-snug tracking-tight text-navy" data-id="element-399" data-mp="1vlp5jf47ljdz">
              {strategy.purpose}
            </p>
            <p className="mt-4 text-[9px] uppercase tracking-micro text-blue" data-id="element-400" data-mp="29dvcqbpdwg1a">
              {strategy.signalsLead}
            </p>
            <ul className="mt-2 flex flex-col gap-1.5" data-id="element-401" data-mp="1gvzqcfza7eup">
              {strategy.signals.map(signal => <li key={signal} className="flex gap-2 text-[13px] leading-snug text-navy" data-id="element-402" data-mp="1bxpue35qga81">
                  <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-blue" aria-hidden="true" data-id="element-403" data-mp="1pk5dnzvr66ru" />
                  {signal}
                </li>)}
            </ul>
            <p className="mt-3 text-[12px] leading-relaxed text-navy2" data-id="element-404" data-mp="1zhb5jac6y1t4">{strategy.signalsNote}</p>
          </div>

          <p className="mt-7 text-[13.5px] leading-relaxed text-navy2" data-id="element-405" data-mp="24a475pcd4u0z">{strategy.valuesLead}</p>

          <div className="mt-4 flex flex-col gap-5" data-id="element-406" data-mp="28a4ww64wwrnc">
            {strategy.values.map(value => <section key={value.number} className="border-t border-line pt-4" data-id="element-407" data-mp="2d7my1acou2ks">
                <h3 className="flex items-baseline gap-2.5 font-display text-[15px] font-semibold tracking-tight text-navy" data-id="element-408" data-mp="2i9vrs8xdx8ny">
                  <span className="font-display text-[12px] font-bold tabular-nums text-blue" data-id="element-409" data-mp="2n5kugl4pb5oh">
                    {value.number}
                  </span>
                  {value.title}
                </h3>
                {value.paragraphs.map(text => <p key={text} className="mt-2.5 text-[12.5px] leading-relaxed text-navy2" data-id="element-410" data-mp="364xrat8573qp">
                    {text}
                  </p>)}
                {value.contrast && <Contrast from={value.contrast.from} to={value.contrast.to} data-id="element-411" data-mp="2xb2o3qqkzndp" />}
              </section>)}
          </div>

          <section className="mt-7 rounded-xl bg-navy px-5 py-5" data-id="element-412" data-mp="2sdsltai3fh8g">
            <p className="text-[9px] uppercase tracking-micro text-mint" data-id="element-413" data-mp="3p3a18h6vohnc">
              {strategy.deeper.kicker}
            </p>
            <h3 className="mt-2 font-display text-[17px] font-semibold leading-snug tracking-tight text-paper" data-id="element-414" data-mp="3l2hr7lq6m8q0">
              {strategy.deeper.title}
            </h3>
            {strategy.deeper.paragraphs.map(text => <p key={text} className="mt-3 text-[12.5px] leading-relaxed text-sky" data-id="element-415" data-mp="3b53d3srqdw0v">
                {text}
              </p>)}
            <div className="mt-4 flex flex-col gap-2 rounded-xl bg-paper/10 px-4 py-3 sm:flex-row sm:items-center" data-id="element-416" data-mp="1znaokq41wa2y">
              <p className="flex-1 text-[12px] leading-snug text-sky/70 line-through decoration-sky/40" data-id="element-417" data-mp="2162a3n3b2lce">
                {strategy.deeper.contrast.from}
              </p>
              <ArrowRightIcon className="h-3.5 w-3.5 shrink-0 text-mint" aria-hidden="true" data-id="element-418" data-mp="1s4kwcbddevqv" />
              <p className="flex-1 text-[12.5px] font-semibold leading-snug text-paper" data-id="element-419" data-mp="1x3dyj7n7j0bq">
                {strategy.deeper.contrast.to}
              </p>
            </div>
          </section>

          <section className="mt-7 border-t border-line pt-5" data-id="element-420" data-mp="1icoow9pe9t9z">
            <p className="text-[9px] uppercase tracking-micro text-mut" data-id="element-421" data-mp="1n6suaoj0ht8y">In one line</p>
            <p className="mt-2 font-display text-[19px] font-semibold leading-snug tracking-tight text-navy" data-id="element-422" data-mp="19f4ikmcod6si">
              {strategy.oneLine}
            </p>
            <p className="mt-3 text-[12.5px] leading-relaxed text-navy2" data-id="element-423" data-mp="zfx601drlg6g">
              {strategy.oneLineSupport}
            </p>
          </section>
        </div>
      </motion.article>
    </motion.div>;
}